import React, { useEffect, useMemo, useRef, useState } from 'react';
import styled, { css } from 'styled-components';
import { motion } from 'framer-motion';
import mq from '../../utils/mq';

const StatItemContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  ${mq.mobile(css`
    //flex-direction: row;
    //align-items: flex-end;
    //justify-content: flex-start;
    //gap: 16px;
  `)};
`;

const StatItemValue = styled.div<{
  $smallVariant: boolean;
  $verySmallVariant: boolean;
}>`
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: center;
  font-size: 52px;

  font-weight: 700;
  line-height: 110%;
  letter-spacing: -1.6px;
  ${mq.mobile(css`
    font-size: 46px;
  `)};
  ${({ $smallVariant }) =>
    $smallVariant &&
    css`
      font-size: 48px;
      ${mq.mobile(css`
        font-size: 36px;
      `)};
    `};
  ${({ $verySmallVariant }) =>
    $verySmallVariant &&
    css`
      font-size: 40px;
      ${mq.mobile(css`
        font-size: 32px;
      `)};
    `};
`;

const Prefix = styled.span`
  color: var(--Foreground-fg-accent, #813dff);
`;

const Value = styled.span`
  color: var(--Foreground-fg-accent, #813dff);
`;

const Suffix = styled.span`
  color: var(--Foreground-fg-accent, #813dff);
`;

const StatItemLabel = styled.div<{
  $smallVariant: boolean;
  $verySmallVariant: boolean;
}>`
  color: var(--Foreground-fg-secondary, #565656);
  text-align: center;
  text-align: center;
  margin-top: 12px;
  font-size: 18px;
  font-style: normal;
  font-weight: 400;
  line-height: 160%;
  letter-spacing: -0.4px;
  ${mq.mobile(css`
    font-size: 18px;
    margin-top: 0;
  `)};
  ${({ $smallVariant }) =>
    $smallVariant &&
    css`
      font-size: 18px;
      ${mq.mobile(css`
        font-size: 18px;
        margin-top: 0;
      `)};
    `};
  ${({ $verySmallVariant }) =>
    $verySmallVariant &&
    css`
      font-size: 16px;
      margin-top: 2px;
      ${mq.mobile(css`
        font-size: 16px;
      `)};
    `};
`;

const MotionValueContainer = styled(motion.div)<{ children: React.ReactNode }>`
  display: inline-block;
`;

interface StatItemProps {
  valuePrefix: string;
  value: string;
  valueSuffix: string;
  subtitle: string;
  startValue?: number;
  durationMs?: number;
  easingFn?: (t: number) => number;
  visibilityThreshold?: number;
  formatNumber?: (n: number) => string;
  triggerOnce?: boolean;
  enableAnimation?: boolean;
  smallVariant?: boolean;
  verySmallVariant?: boolean;
}

const defaultNumberFormatter = new Intl.NumberFormat('en-GB');

export default function StatItem({
  valuePrefix,
  value,
  valueSuffix,
  subtitle,
  startValue,
  durationMs,
  easingFn,
  visibilityThreshold,
  formatNumber,
  triggerOnce,
  enableAnimation,
  smallVariant = false,
  verySmallVariant = false
}: StatItemProps) {
  const threshold = visibilityThreshold ?? 0.25;
  const duration = Math.max(0, durationMs ?? 3000);
  const ease = useMemo<(t: number) => number>(() => {
    if (easingFn) return easingFn;
    return function easeOutCubic(t: number) {
      return 1 - Math.pow(1 - t, 3);
    };
  }, [easingFn]);

  const parseNumeric = function (raw: string): number | null {
    const n = Number(String(raw).replace(/,/g, ''));
    return Number.isFinite(n) ? n : null;
  };

  const target = parseNumeric(value);
  const from = Number.isFinite(startValue as number)
    ? (startValue as number)
    : 0;
  const formatter =
    formatNumber ??
    function (n: number) {
      return defaultNumberFormatter.format(n);
    };
  const shouldAnimate = enableAnimation !== false && Number.isFinite(target);

  const containerRef = useRef<HTMLDivElement | null>(null);
  const [hasStarted, setHasStarted] = useState(false);
  const [displayValue, setDisplayValue] = useState<number>(
    shouldAnimate ? from : target ?? 0
  );

  useEffect(() => {
    if (!shouldAnimate) {
      setHasStarted(true);
      setDisplayValue(target ?? 0);
      return;
    }
    if (!containerRef.current || hasStarted || !Number.isFinite(target)) return;
    if (
      typeof window === 'undefined' ||
      typeof IntersectionObserver === 'undefined'
    ) {
      setHasStarted(true);
      return;
    }
    const node = containerRef.current;
    const observer = new IntersectionObserver(
      function (entries) {
        for (let i = 0; i < entries.length; i++) {
          const e = entries[i];
          if (
            (e.isIntersecting && e.intersectionRatio >= threshold) ||
            e.intersectionRatio >= threshold
          ) {
            setHasStarted(true);
            if (triggerOnce !== false) observer.disconnect();
            break;
          }
        }
      },
      { threshold }
    );
    observer.observe(node);
    return function cleanup() {
      observer.disconnect();
    };
  }, [hasStarted, shouldAnimate, target, threshold, triggerOnce]);

  useEffect(() => {
    if (!hasStarted || !Number.isFinite(target)) return;
    if (duration === 0) {
      setDisplayValue(target as number);
      return;
    }
    let raf = 0;
    const startTs =
      typeof performance !== 'undefined' ? performance.now() : Date.now();
    const fromVal = from;
    const toVal = target as number;
    function tick(now: number) {
      const elapsed = now - startTs;
      const p = Math.max(0, Math.min(1, elapsed / duration));
      const eased = ease(p);
      const current = fromVal + (toVal - fromVal) * eased;
      setDisplayValue(Math.round(current));
      if (p < 1) raf = requestAnimationFrame(tick);
      else setDisplayValue(Math.round(toVal));
    }
    raf = requestAnimationFrame(tick);
    return function cancel() {
      cancelAnimationFrame(raf);
    };
  }, [hasStarted, target, duration, ease, from]);

  return (
    <StatItemContainer ref={containerRef}>
      <MotionValueContainer
        initial={{ opacity: 0, y: 8 }}
        animate={hasStarted ? { opacity: 1, y: 0 } : { opacity: 0, y: 8 }}
        transition={{ duration: 0.4 }}
      >
        <StatItemValue
          $smallVariant={smallVariant}
          $verySmallVariant={verySmallVariant}
        >
          <Prefix>{valuePrefix}</Prefix>
          <Value>
            {Number.isFinite(target) ? formatter(displayValue) : value}
          </Value>
          <Suffix>{valueSuffix}</Suffix>
        </StatItemValue>
      </MotionValueContainer>
      <StatItemLabel
        $smallVariant={smallVariant}
        $verySmallVariant={verySmallVariant}
      >
        {subtitle}
      </StatItemLabel>
    </StatItemContainer>
  );
}
